import React, { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import type { NotificationSetting, SendCountSummary } from '@/services/notificationSettingsService';
import { ALWAYS_ON_NOTIFICATION_TYPES } from './config';

interface MasterListProps {
  settings: NotificationSetting[];
  selectedType: string | null;
  query: string;
  onQueryChange: (query: string) => void;
  counts: Record<string, SendCountSummary>;
  countsError?: string | null;
  togglingType: string | null;
  onSelect: (setting: NotificationSetting) => void;
  onToggle: (type: string, enabled: boolean) => void;
}

type Filter = 'all' | 'enabled' | 'disabled' | 'borrower' | 'team';

function triggerTag(setting: NotificationSetting): string {
  const category = setting.category?.replace(/[_-]/g, ' ') || 'Automatic';
  if (category.toLowerCase().includes('appraisal')) {
    return 'Document Upload';
  }
  if (category.toLowerCase().includes('compensation')) {
    return 'Status Change';
  }
  if (category.toLowerCase().includes('disclosure')) {
    return 'Field Populated';
  }
  return category.split(/\s+/).slice(0, 2).join(' ') || 'Automatic';
}

function isBorrowerFacing(setting: NotificationSetting): boolean {
  const haystack = `${setting.recipients} ${setting.name} ${setting.notification_type}`.toLowerCase();
  return haystack.includes('borrower') || haystack.includes('consumer');
}

function rowLastSent(summary: SendCountSummary | undefined, countsError?: string | null): string {
  if (countsError) {
    return 'Unavailable';
  }
  if (!summary?.lastSent) {
    return '-';
  }
  const sent = new Date(summary.lastSent);
  const today = new Date();
  if (sent.toDateString() === today.toDateString()) {
    return 'Today';
  }
  const diffDays = Math.floor((today.getTime() - sent.getTime()) / 86_400_000);
  if (diffDays === 1) {
    return 'Yesterday';
  }
  if (diffDays > 1 && diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return sent.toLocaleDateString();
}

export function MasterList({
  settings,
  selectedType,
  query,
  onQueryChange,
  counts,
  countsError,
  togglingType,
  onSelect,
  onToggle
}: MasterListProps) {
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => settings.filter((setting) => {
    if (filter === 'enabled' && !setting.is_enabled) {
      return false;
    }
    if (filter === 'disabled' && setting.is_enabled) {
      return false;
    }
    if (filter === 'borrower' && !isBorrowerFacing(setting)) {
      return false;
    }
    if (filter === 'team' && isBorrowerFacing(setting)) {
      return false;
    }
    const haystack = `${setting.name} ${setting.notification_type} ${setting.category} ${setting.recipients} ${setting.trigger_description}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  }), [filter, query, settings]);

  const grouped = filtered.reduce<Record<string, NotificationSetting[]>>((acc, setting) => {
    acc[setting.category] = acc[setting.category] || [];
    acc[setting.category].push(setting);
    return acc;
  }, {});

  const totalEnabled = settings.filter((setting) => setting.is_enabled).length;

  const toggleSelected = (type: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const bulkToggle = (enabled: boolean) => {
    for (const type of selectedTypes) {
      // Mandatory types have no off switch; the database refuses is_enabled = false.
      if (ALWAYS_ON_NOTIFICATION_TYPES.has(type)) {
        continue;
      }
      onToggle(type, enabled);
    }
    setSelectedTypes(new Set());
  };

  return (
    <aside className="en-master">
      <div className="en-master-head">
        <div className="en-master-title-row">
          <div className="en-master-title">Email Notifications</div>
          <div className="en-master-count">{totalEnabled}/{settings.length} on</div>
        </div>
        <div className="en-master-sub">Internal team alerts and borrower communications. Triggers fire automatically against Encompass events.</div>
        <div className="en-search">
          <Search className="h-3.5 w-3.5" />
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search notifications, triggers, recipients..."
          />
        </div>
        <div className="en-filter-chips">
          <button type="button" className={`en-filter-chip ${filter === 'all' ? 'is-active' : ''}`} onClick={() => setFilter('all')}>
            All <span className="en-chip-count">{settings.length}</span>
          </button>
          <button type="button" className={`en-filter-chip ${filter === 'enabled' ? 'is-active' : ''}`} onClick={() => setFilter('enabled')}>
            Enabled <span className="en-chip-count">{totalEnabled}</span>
          </button>
          <button type="button" className={`en-filter-chip ${filter === 'disabled' ? 'is-active' : ''}`} onClick={() => setFilter('disabled')}>
            Disabled <span className="en-chip-count">{settings.length - totalEnabled}</span>
          </button>
          <button type="button" className={`en-filter-chip ${filter === 'borrower' ? 'is-active' : ''}`} onClick={() => setFilter('borrower')}>To borrower</button>
          <button type="button" className={`en-filter-chip ${filter === 'team' ? 'is-active' : ''}`} onClick={() => setFilter('team')}>Team only</button>
        </div>
        {countsError && <div className="en-warning mt-2">Counts unavailable. Check notification Activity deployment.</div>}
      </div>
      <div className="en-master-list">
        {filtered.length === 0 && (
          <div className="en-empty">
            <Search className="mx-auto mb-2 h-7 w-7" />
            <div className="font-semibold">No matches</div>
            <div>Try a different search or clear filters.</div>
          </div>
        )}
        {Object.entries(grouped).map(([category, categorySettings]) => (
          <section key={category}>
            <div className="en-cat-header">
              <span>{category}</span>
              <span className="en-cat-count">{categorySettings.filter((setting) => setting.is_enabled).length} of {categorySettings.length} on</span>
            </div>
            {categorySettings.map((setting) => {
              const summary = counts[setting.notification_type];
              const selected = selectedTypes.has(setting.notification_type);
              return (
                <div
                  key={setting.notification_type}
                  role="button"
                  tabIndex={0}
                  className={`en-row ${selectedType === setting.notification_type ? 'is-selected' : ''}`}
                  onClick={() => onSelect(setting)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelect(setting);
                    }
                  }}
                >
                  <button
                    type="button"
                    aria-label={`Select ${setting.name}`}
                    className={`en-row-check ${selected ? 'is-checked' : ''}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleSelected(setting.notification_type);
                    }}
                  >
                    {selected && <Check className="h-2.5 w-2.5" />}
                  </button>
                  <div className="en-row-body">
                    <div className="en-row-title">{setting.name}</div>
                    <div className="en-row-sub">
                      <span className="en-tag">{triggerTag(setting)}</span>
                    </div>
                  </div>
                  <div className="en-row-meta">
                    <strong>{countsError ? '-' : summary?.countAllTime ?? summary?.count ?? 0}</strong>
                    <span>{rowLastSent(summary, countsError)}</span>
                  </div>
                  {ALWAYS_ON_NOTIFICATION_TYPES.has(setting.notification_type) ? (
                    <span className="en-status-pill is-success">Always on</span>
                  ) : (
                    <div onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={setting.is_enabled}
                        aria-label={setting.is_enabled ? `Disable ${setting.name}` : `Enable ${setting.name}`}
                        className={`en-toggle sm ${setting.is_enabled ? 'is-on' : ''}`}
                        data-preserve-disabled
                        disabled={togglingType === setting.notification_type}
                        onClick={() => onToggle(setting.notification_type, !setting.is_enabled)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        ))}
      </div>
      {selectedTypes.size > 0 && (
        <div className="en-bulkbar">
          <strong>{selectedTypes.size}</strong>
          <span>selected</span>
          <span className="spacer" />
          <button type="button" onClick={() => bulkToggle(true)}>Enable all</button>
          <button type="button" onClick={() => bulkToggle(false)}>Disable all</button>
          <button type="button" onClick={() => setSelectedTypes(new Set())}>Clear</button>
        </div>
      )}
    </aside>
  );
}
